import { useState } from 'react';
import {
  Body3,
  Box,
  ButtonColorVariant,
  Color,
  Heading5,
  ModalButton,
  Space,
  useModal,
} from '@gousto-internal/citrus-react';
import type { RecipeDTOTypes } from '@repo/experience-api-client';
import { useExperienceApiClient } from '../../hooks/useExperienceApiClient';

type CookingSectionProps = {
  recipeId: string;
  cookingInstructionsModalName: string;
  onStepsFetched: (steps: readonly RecipeDTOTypes.CookingStepDTO[]) => void;
};

export function CookingSection({
  recipeId,
  cookingInstructionsModalName,
  onStepsFetched,
}: CookingSectionProps) {
  const client = useExperienceApiClient();
  const { openModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  async function seeCookingSteps() {
    setIsLoading(true);
    setHasError(false);
    const { data } = await client.GET('/public/v1/recipes/{recipeId}/cooking-steps', {
      params: { path: { recipeId } },
    });
    setIsLoading(false);

    const steps = data?.data?.steps;
    if (!steps?.length) {
      setHasError(true);
      return;
    }

    // `imageUrl` codegens as `Record<string, never> | null` instead of `string | null` — see
    // packages/experience-api-client/src/types/recipe.ts.
    onStepsFetched(steps as unknown as RecipeDTOTypes.CookingStepDTO[]);
    openModal(cookingInstructionsModalName);
  }

  return (
    <Box>
      <Heading5>Cooking</Heading5>
      <Space size={4} />
      <ModalButton
        colorVariant={ButtonColorVariant.Secondary}
        onClick={seeCookingSteps}
        disabled={isLoading}
      >
        See cooking steps
      </ModalButton>
      {hasError && (
        <>
          <Space size={2} />
          <Body3 color={Color.Error_800}>
            Something went wrong loading the cooking steps. Please try again in a moment.
          </Body3>
        </>
      )}
    </Box>
  );
}
